import React from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/shared/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Phone, LogIn } from "lucide-react";

export default function MerchantLogin() {
    const { user, status, persistSession } = useAuth();
    const navigate = useNavigate();
    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm({ defaultValues: { mobile_number: "", password: "" } });

    if (status === "authenticated" && user) {
        setTimeout(() => {
            if (user.role === "SHOP_OWNER") navigate("/merchant/home", { replace: true });
            else navigate("/dashboard", { replace: true });
        }, 0);
        return null;
    }

    const onSubmit = async (data) => {
        try {
            const mobile = (data.mobile_number || "").replace(/\s|-/g, "");
            const res = await api.post("/auth/shop/login-mobile", {
                mobile_number: mobile,
                password: data.password,
            });
            persistSession(res.data);
            toast.success("Welcome back!");
            // Route below is decided by merchant redirect logic in App.js
            const u = res.data.user;
            const docs = u?.verification_docs || {};
            const hasDocs = docs.shop_business_license && docs.pan_or_aadhaar;
            if (!hasDocs) return navigate("/merchant/onboarding", { replace: true });
            if (u.status === "PENDING_APPROVAL") return navigate("/pending", { replace: true });
            if (u.status === "REJECTED") return navigate("/rejected", { replace: true });
            navigate("/merchant/home", { replace: true });
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };

    return (
        <div className="min-h-screen bg-neutral-50" data-testid="merchant-login-page">
            <div className="mx-auto flex min-h-screen max-w-[560px] flex-col bg-white">
                <header className="flex flex-col items-center gap-2 border-b border-neutral-200 bg-white px-4 py-6">
                    <Logo size={88} variant="lockup" className="sm:hidden" />
                    <Logo size={120} variant="lockup" className="hidden sm:block" />
                </header>
                <main className="flex flex-1 flex-col justify-center px-6 py-6">
                    <div>
                        <h1 className="text-2xl font-extrabold text-neutral-900">
                            Sign in to your shop
                        </h1>
                        <p className="mt-1 text-sm text-neutral-500">
                            Use the mobile number you registered with.
                        </p>
                    </div>
                    <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-4" data-testid="merchant-login-form">
                        <div>
                            <Label className="text-xs">Mobile number</Label>
                            <div className="relative mt-1">
                                <Phone
                                    size={16}
                                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
                                />
                                <Input
                                    className="pl-9 h-12 text-base"
                                    placeholder="98765 43210"
                                    inputMode="numeric"
                                    autoComplete="tel"
                                    data-testid="ml-mobile"
                                    {...register("mobile_number", {
                                        required: true,
                                        minLength: 10,
                                    })}
                                />
                            </div>
                            {errors.mobile_number && (
                                <p className="mt-1 text-xs text-rose-600">Enter a valid mobile number</p>
                            )}
                        </div>
                        <div>
                            <Label className="text-xs">Password</Label>
                            <Input
                                className="mt-1 h-12 text-base"
                                type="password"
                                placeholder="••••••••"
                                autoComplete="current-password"
                                data-testid="ml-password"
                                {...register("password", { required: true, minLength: 8 })}
                            />
                            {errors.password && (
                                <p className="mt-1 text-xs text-rose-600">Password is required</p>
                            )}
                        </div>

                        <Button
                            type="submit"
                            disabled={isSubmitting}
                            className="h-12 w-full nest-gradient text-base font-semibold text-white"
                            data-testid="ml-submit"
                        >
                            <LogIn size={16} className="mr-2" />
                            {isSubmitting ? "Signing in…" : "Login"}
                        </Button>
                    </form>

                    <div className="mt-6 rounded-2xl border border-orange-100 bg-orange-50 p-4 text-center text-sm text-neutral-700">
                        New here?{" "}
                        <Link to="/merchant/register" className="font-semibold text-orange-600" data-testid="ml-register-link">
                            Create your shop account
                        </Link>
                    </div>

                    <p className="mt-6 text-center text-xs text-neutral-400">
                        Are you internal team?{" "}
                        <Link to="/login" className="font-semibold text-neutral-600">
                            Staff sign in
                        </Link>
                    </p>
                </main>
            </div>
        </div>
    );
}
